import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Bandaids, Warning, CheckCircle } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tool to audit and repair ELO data inconsistencies.
 *
 * Catches three classes of historical corruption from the pre-Feb-2026
 * double-apply bug:
 *   1. Duplicate elo_history rows for the same (player, match)
 *   2. Drift between current elo_rating and the latest elo_history row
 *   3. Zero-sum violations on completed matches
 */
export default function EloConsistencyCard() {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [fixing, setFixing] = useState(false);

  const runAudit = async () => {
    setRunning(true);
    try {
      const r = await axios.get(`${API}/admin/elo-consistency`, { withCredentials: true });
      setReport(r.data);
      toast.success(
        r.data.summary === "CLEAN"
          ? "All ELO data is consistent"
          : `Found: ${r.data.duplicate_history_count} dup(s), ${r.data.drift_count} drift, ${r.data.violations_count} zero-sum violation(s)`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Audit failed");
    } finally {
      setRunning(false);
    }
  };

  const runFix = async () => {
    if (!window.confirm(
      "Apply the ELO consistency fix?\n\n" +
      "This will:\n" +
      "• Remove duplicate elo_history rows (keeps oldest)\n" +
      "• Recompute elo_rating from cleaned history\n" +
      "• Snapshot pre-fix values — rollback available via the Rebaseline Premier ELO → Rollback button.",
    )) return;
    setFixing(true);
    try {
      const r = await axios.post(`${API}/admin/elo-consistency/fix`, {}, { withCredentials: true });
      toast.success(
        `Fix applied — ${r.data.duplicate_rows_removed} dup rows removed, ${r.data.players_reconciled} players reconciled`,
      );
      await runAudit(); // refresh
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fix failed");
    } finally {
      setFixing(false);
    }
  };

  const isClean = report?.summary === "CLEAN";

  return (
    <Card className="bg-card border-border" data-testid="elo-consistency-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="w-5 h-5 text-secondary" weight="duotone" />
              ELO Consistency
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Audit for historical ELO double-apply bugs and repair any affected player ratings.
              Never touches match results or placements.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline" onClick={runAudit} disabled={running}
            className="border-secondary/40 text-secondary hover:bg-secondary/10"
            data-testid="elo-audit-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-1.5" weight="fill" />
            {running ? "Auditing…" : "Run Audit"}
          </Button>
          {report && !isClean && (
            <Button
              size="sm" onClick={runFix} disabled={fixing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="elo-fix-btn"
            >
              <Bandaids className="w-4 h-4 mr-1.5" weight="fill" />
              {fixing ? "Fixing…" : "Apply Fix"}
            </Button>
          )}
        </div>

        {report && (
          <div className="rounded-lg border border-border bg-muted/10 p-3 space-y-2" data-testid="elo-audit-report">
            <div className="flex items-center gap-2">
              {isClean ? (
                <CheckCircle className="w-4 h-4 text-primary" weight="fill" />
              ) : (
                <Warning className="w-4 h-4 text-destructive" weight="fill" />
              )}
              <span className={`text-sm font-semibold ${isClean ? "text-primary" : "text-destructive"}`}>
                {isClean ? "All consistent" : "Issues detected"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="rounded px-2 py-1.5 bg-background border border-border">
                <p className="text-muted-foreground">Duplicate rows</p>
                <p className="font-heading font-bold text-lg tabular-nums">{report.duplicate_history_count}</p>
              </div>
              <div className="rounded px-2 py-1.5 bg-background border border-border">
                <p className="text-muted-foreground">ELO drift</p>
                <p className="font-heading font-bold text-lg tabular-nums">{report.drift_count}</p>
              </div>
              <div className="rounded px-2 py-1.5 bg-background border border-border">
                <p className="text-muted-foreground">Zero-sum errors</p>
                <p className="font-heading font-bold text-lg tabular-nums">{report.violations_count}</p>
              </div>
            </div>
            {report.current_vs_history_drift?.length > 0 && (
              <div className="pt-2 border-t border-border">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Drifted players</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {report.current_vs_history_drift.slice(0, 20).map((d) => (
                    <div key={d.player_id} className="flex items-center justify-between text-xs">
                      <span>{d.name}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {d.current_elo} vs {d.last_history_elo}
                        <span className={`ml-2 font-bold ${d.drift > 0 ? "text-primary" : "text-destructive"}`}>
                          {d.drift > 0 ? "+" : ""}{d.drift}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
